import { useState, useEffect } from 'react';
import { 
  Star, MessageSquare, Send, RefreshCw, Trash2, 
  User, Calendar, ChevronDown, ChevronUp, Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

// Star Rating Display
const StarRating = ({ rating, size = 'md' }) => {
  const sizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizes[size]} ${
            star <= rating 
              ? 'text-amber-400 fill-amber-400' 
              : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

// Rating Stats Card
const RatingStatsCard = ({ stats, isDarkMode, language }) => {
  const total = stats?.total_reviews || 0;
  const avgRating = stats?.average_rating || 0;
  const distribution = stats?.rating_distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  
  const maxCount = Math.max(...Object.values(distribution), 1);
  
  return (
    <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-[#0F172A] border-white/10' : 'bg-[#F8FAFC] border-gray-200'}`}>
      <div className="flex items-start gap-6">
        {/* Overall Rating */}
        <div className="text-center">
          <div className={`text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {avgRating.toFixed(1)}
          </div>
          <StarRating rating={Math.round(avgRating)} size="lg" />
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {total} {language === 'ar' ? 'تقييم' : 'reviews'}
          </p>
        </div>
        
        {/* Rating Distribution */}
        <div className="flex-1 space-y-2">
          {[5, 4, 3, 2, 1].map((rating) => (
            <div key={rating} className="flex items-center gap-2">
              <span className={`text-sm w-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {rating} <Star className="h-3 w-3 inline text-amber-400 fill-amber-400" />
              </span>
              <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div 
                  className="h-full rounded-full bg-amber-400 transition-all duration-300"
                  style={{ width: `${(distribution[rating] / maxCount) * 100}%` }}
                />
              </div>
              <span className={`text-sm w-8 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {distribution[rating]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review, language, isDarkMode, onReply, onDeleteReply, replying }) => {
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    onReply(review.id, replyText);
    setReplyText('');
    setShowReplyInput(false);
  };

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'
    }`}>
      {/* Review Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#1E9BD9]/10'
            }`}>
              {review.user_avatar ? (
                <img src={review.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="h-5 w-5 text-[#1E9BD9]" />
              )}
            </div>
            
            <div>
              <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {review.user_name}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <StarRating rating={review.rating} size="sm" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Calendar className="h-3 w-3 inline me-1" />
                  {formatDate(review.created_at)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Rating Badge */}
          <div className={`px-3 py-1 rounded-full ${
            review.rating >= 4 
              ? 'bg-green-100 text-green-700' 
              : review.rating >= 3 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-red-100 text-red-700'
          }`}>
            <span className="font-bold">{review.rating}</span>
            <Star className="h-3 w-3 inline ms-0.5 fill-current" />
          </div>
        </div>
        
        {/* Comment */}
        {review.comment && (
          <p className={`mt-3 text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {review.comment}
          </p>
        )}
        
        {/* Reply Section */}
        {review.reply && (
          <div className={`mt-4 p-3 rounded-lg border-r-4 border-[#1E9BD9] ${
            isDarkMode ? 'bg-[#0F172A]' : 'bg-[#F0F9FF]'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-medium ${isDarkMode ? 'text-[#38BDF8]' : 'text-[#1E9BD9]'}`}>
                {language === 'ar' ? 'ردك:' : 'Your reply:'}
              </span>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatDate(review.reply_date)}
                </span>
                <button
                  onClick={() => onDeleteReply(review.id)}
                  className="text-red-500 hover:text-red-600"
                  title={language === 'ar' ? 'حذف الرد' : 'Delete reply'}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {review.reply}
            </p>
          </div>
        )}
        
        {/* Reply Input */}
        {!review.reply && (
          <>
            {showReplyInput ? (
              <div className="mt-4 space-y-2">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={language === 'ar' ? 'اكتب ردك هنا...' : 'Write your reply...'}
                  className={`text-sm ${isDarkMode ? 'bg-[#0F172A] border-white/10' : ''}`}
                  rows={3}
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowReplyInput(false);
                      setReplyText('');
                    }}
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmitReply}
                    disabled={!replyText.trim() || replying}
                    className="bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                  >
                    <Send className="h-3.5 w-3.5 me-1" />
                    {language === 'ar' ? 'إرسال' : 'Send'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReplyInput(true)}
                className={`mt-3 ${isDarkMode ? 'text-[#38BDF8] hover:text-white' : 'text-[#1E9BD9]'}`}
              >
                <MessageSquare className="h-4 w-4 me-1" />
                {language === 'ar' ? 'رد على التقييم' : 'Reply'}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Main Reviews Tab Component
export const ReviewsTab = ({ storeId, language, isDarkMode, BACKEND_URL }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });

  const fetchReviews = async (page = 1, rating = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('business_token');
      let url = `${BACKEND_URL}/api/merchant/stores/${storeId}/reviews?page=${page}&limit=10`;
      if (rating && rating !== 'all') {
        url += `&rating=${rating}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReviews(response.data.reviews || []);
      setStats(response.data.stats || null);
      setPagination(response.data.pagination || { page: 1, total: 0, pages: 1 });
    } catch (error) {
      console.error('Error fetching reviews:', error);
      if (error.response?.status === 401) {
        toast.error(language === 'ar' ? 'انتهت صلاحية الجلسة' : 'Session expired');
      } else {
        toast.error(language === 'ar' ? 'خطأ في جلب التقييمات' : 'Error fetching reviews');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeId) {
      fetchReviews(1, filter === 'all' ? null : filter);
    }
  }, [storeId, filter]);

  const handleReply = async (reviewId, replyText) => {
    setReplying(true);
    try {
      const token = localStorage.getItem('business_token');
      await axios.post(
        `${BACKEND_URL}/api/merchant/reviews/${reviewId}/reply`,
        { reply: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'ar' ? 'تم إضافة الرد بنجاح' : 'Reply added successfully');
      fetchReviews(pagination.page, filter === 'all' ? null : filter);
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error(language === 'ar' ? 'خطأ في إضافة الرد' : 'Error adding reply');
    } finally {
      setReplying(false);
    }
  };

  const handleDeleteReply = async (reviewId) => {
    if (!confirm(language === 'ar' ? 'هل تريد حذف هذا الرد؟' : 'Delete this reply?')) return;
    
    try {
      const token = localStorage.getItem('business_token');
      await axios.delete(
        `${BACKEND_URL}/api/merchant/reviews/${reviewId}/reply`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'ar' ? 'تم حذف الرد' : 'Reply deleted');
      fetchReviews(pagination.page, filter === 'all' ? null : filter);
    } catch (error) {
      console.error('Error deleting reply:', error);
      toast.error(language === 'ar' ? 'خطأ في حذف الرد' : 'Error deleting reply');
    }
  };

  const filters = [
    { id: 'all', label: language === 'ar' ? 'الكل' : 'All' },
    { id: '5', label: '5 ⭐' },
    { id: '4', label: '4 ⭐' },
    { id: '3', label: '3 ⭐' },
    { id: '2', label: '2 ⭐' },
    { id: '1', label: '1 ⭐' }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Card */}
      {stats && (
        <RatingStatsCard stats={stats} isDarkMode={isDarkMode} language={language} />
      )}
      
      {/* Reviews List Card */}
      <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            <MessageSquare className="h-5 w-5 text-[#1E9BD9]" />
            {language === 'ar' ? 'التقييمات' : 'Reviews'}
            {pagination.total > 0 && (
              <span className={`text-sm font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ({pagination.total})
              </span>
            )}
          </h2>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchReviews(pagination.page, filter === 'all' ? null : filter)}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                filter === f.id
                  ? isDarkMode 
                    ? 'bg-[#38BDF8] text-white' 
                    : 'bg-[#1E9BD9] text-white'
                  : isDarkMode
                    ? 'bg-white/5 text-gray-400 hover:bg-white/10'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className={`h-8 w-8 mx-auto mb-4 animate-spin ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
            </p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <Star className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} />
            <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {language === 'ar' ? 'لا توجد تقييمات بعد' : 'No reviews yet'}
            </p>
            <p className={isDarkMode ? 'text-gray-400' : 'text-[#475569]'}>
              {language === 'ar' ? 'ستظهر تقييمات العملاء هنا' : 'Customer reviews will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                language={language}
                isDarkMode={isDarkMode}
                onReply={handleReply}
                onDeleteReply={handleDeleteReply}
                replying={replying}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => fetchReviews(page, filter === 'all' ? null : filter)}
                className={`w-10 h-10 rounded-lg font-medium ${
                  page === pagination.page
                    ? 'bg-[#1E9BD9] text-white'
                    : isDarkMode
                      ? 'bg-white/10 text-gray-400 hover:bg-white/20'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsTab;
